/**
 * Wave Configuration Utilities
 * Centralized wave configuration management - fetches from backend API
 */
import { apiGet } from './apiClient';

export interface WaveGroup {
  id: string;
  name: string;
  description: string;
  range: {
    min: number;
    max: number;
  };
  isDefault: boolean;
  isAutoLoaded: boolean;
  presetType: 'static' | 'dynamic';
  goldScenarioKey: string;
  calculationFunction: string | null;
  waveNumbers: number[];
}

export interface WaveConfig {
  // Legacy fields for backward compatibility
  defaultWaveRange: {
    min: number;
    max: number;
  };
  maxWaves: number;
  defaultWaveNumbers: number[];
  
  // New structure - multiple wave groups
  waveGroups: WaveGroup[];
  
  // Enhanced structure - group status and scenario info
  defaultGoldScenario: {
    key: string;
    name: string;
    description: string;
    goldEvents: {
      cspm: number;
      cspmRanges: any[];
      kills: any[];
      turrets: any[];
      plates: any[];
      miscellaneous: any[];
    };
  };
  autoLoadedGroups: string[];
}



let cachedWaveConfig: WaveConfig | null = null;

/**
 * Fetch wave configuration from backend (single source of truth)
 */
export const getWaveConfig = async (): Promise<WaveConfig> => {
  // Return cached config if available
  if (cachedWaveConfig) {
    return cachedWaveConfig;
  }

  try {
    console.log('[WaveConfig] 📋 Fetching wave configuration from backend...');
    
    const result = await apiGet('/api/static/wave/config');
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch wave config');
    }
    
    cachedWaveConfig = result.data;
    console.log('[WaveConfig] ✅ Wave configuration loaded:', cachedWaveConfig);
    
    return cachedWaveConfig!; // We know it's not null because we just assigned it
    
  } catch (error) {
    console.error('[WaveConfig] ❌ Failed to fetch wave configuration:', error);
    console.error('[WaveConfig] 🚫 No fallback - throwing error to caller');
    
    // No fallback - let the caller handle the failure
    throw new Error(`Failed to fetch wave configuration from backend: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get default wave numbers array
 */
export const getDefaultWaveNumbers = async (): Promise<number[]> => {
  const config = await getWaveConfig();
  return config.defaultWaveNumbers;
};

/**
 * Get default wave range
 */
export const getDefaultWaveRange = async (): Promise<{ min: number; max: number }> => {
  const config = await getWaveConfig();
  return config.defaultWaveRange;
};

/**
 * Get all wave groups
 */
export const getWaveGroups = async (): Promise<WaveGroup[]> => {
  const config = await getWaveConfig();
  return config.waveGroups;
};

/**
 * Get default wave group (used for auto-creation)
 */
export const getDefaultWaveGroup = async (): Promise<WaveGroup | undefined> => {
  const config = await getWaveConfig();
  return config.waveGroups.find(group => group.isDefault);
};

/**
 * Get wave group by ID
 */
export const getWaveGroupById = async (id: string): Promise<WaveGroup | undefined> => {
  const config = await getWaveConfig();
  return config.waveGroups.find(group => group.id === id);
};

/**
 * Get auto-loaded groups (groups that are automatically loaded on mount)
 */
export const getAutoLoadedGroups = async (): Promise<WaveGroup[]> => {
  const config = await getWaveConfig();
  return config.waveGroups.filter(group => group.isAutoLoaded);
};

/**
 * Get exportable groups (groups that can be manually exported)
 */
export const getExportableGroups = async (): Promise<WaveGroup[]> => {
  const config = await getWaveConfig();
  return config.waveGroups.filter(group => !group.isAutoLoaded);
};

/**
 * Get default gold scenario configuration
 */
export const getDefaultGoldScenario = async (): Promise<WaveConfig['defaultGoldScenario']> => {
  const config = await getWaveConfig();
  return config.defaultGoldScenario;
};

/**
 * Check if a group is auto-loaded
 */
export const isGroupAutoLoaded = async (groupId: string): Promise<boolean> => {
  const config = await getWaveConfig();
  return config.autoLoadedGroups.includes(groupId);
};

/**
 * Clear cached configuration (useful for testing or when backend config changes)
 */
export const clearWaveConfigCache = (): void => {
  cachedWaveConfig = null;
  console.log('[WaveConfig] 🗑️ Wave configuration cache cleared');
};
